import { reactive, UnwrapRef, Ref, ref, ComponentInternalInstance, getCurrentInstance } from '@vue/composition-api';
import dayjs from 'dayjs';
import { i18n_t } from '@/locales/i18n';
import smh from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import { copyText2Clipboard, formatSize } from '@/renderer/scripts/utils/tools/index';
import store from '@/renderer/store';
import { getCustomDomainOrWebUrl } from '@/renderer/scripts/utils/CustomDomain/customDomain';
import { DomainItem } from '@/renderer/scripts/libs/smh-sdk-class/typings';

export enum GenerateCodeStatus {
  NOT_GENERATED = 'NOT_GENERATED',
  IS_STOP = 'IS_STOP',
  IS_START = 'IS_START',
}

export interface InviteUrlInfo {
  codeStatus: GenerateCodeStatus;
  expireTime: string;
  expired: boolean;
  enabled: boolean;
  code: string;
  domains: DomainItem[];
  groupName: string;
  invitedCount: number;
  userLimitReached: boolean;
  allowPersonalSpace: boolean;
  personalSpaceQuotaSize: string;
}

interface SpaceConfig {
  allowPersonalSpace: boolean;
  personalSpaceQuotaSize: string;
}
interface SpaceSizeInfo extends SpaceConfig {
  orgAvailableSpace: string;
  orgAvailableSpaceInfo: {
    size: number;
    unit: string;
  };
}
type Handler = {
  loadingUrlInfo: Ref<boolean>;
  inviteUrlInfo: UnwrapRef<InviteUrlInfo>;
  spaceSizeInfo: SpaceSizeInfo;
  methods: {
    initData: (immediately?: boolean) => void;
    initInternalData: () => void;
    checkIfHasInviteUrl: () => Promise<void>;
    getInviteCodeInfo: (code: string) => Promise<void>;
    generateInviteUrl: () => Promise<void>;
    updateInviteCodeInfo: (type: 'stop' | 'recovery' | string | undefined, _params?: Object) => Promise<void>;
  };
};
export default function handler(): Handler {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  // 当前组件
  const inviteUrlInfo = reactive({
    codeStatus: GenerateCodeStatus.NOT_GENERATED,
    expireTime: '',
    code: '',
    domains: [
      {
        domain: '',
        isCustom: false,
        status: 0,
      },
    ],
    enabled: true,
    expired: false,
    invitedCount: 0,
    userLimitReached: false,
    groupName: '',
    allowPersonalSpace: false,
    personalSpaceQuotaSize: '',
  });
  const loadingUrlInfo = ref(false);
  const { defaultUserOptions } = vm.$store.getters;

  // 空间设置 默认为初始化设置
  const spaceSizeInfo = reactive({
    allowPersonalSpace: defaultUserOptions.allowPersonalSpace,
    personalSpaceQuotaSize: defaultUserOptions.personalSpaceQuotaSize,
    orgAvailableSpace: '0',
    orgAvailableSpaceInfo: {
      size: 0,
      unit: 'B',
    },
  });
  const methods = {
    async initData(immediately?: boolean) {
      loadingUrlInfo.value = true;
      await methods.checkIfHasInviteUrl(immediately);
      // 获取企业剩余额度
      const { availableSpace } = await store.dispatch('GetOrgSpace');
      spaceSizeInfo.orgAvailableSpace = availableSpace;
      const spaceInfo = formatSize(availableSpace);
      spaceSizeInfo.orgAvailableSpaceInfo = {
        size: spaceInfo.size,
        unit: spaceInfo.unit,
      };
      loadingUrlInfo.value = false;
    },
    // 初始化当前已邀请的企业成员
    initInternalData() {
      methods.checkIfHasInviteUrl();
    },
    async generateInviteUrl(immediately?: boolean) {
      try {
        const params = {
          role: 'user',
          allowPersonalSpace: spaceSizeInfo.allowPersonalSpace,
          personalSpaceQuotaSize: spaceSizeInfo.personalSpaceQuotaSize,
        };
        const {
          data: { code, domains },
        } = await smh.userAPI.generateInviteCode(params);
        await methods.getInviteCodeInfo(code);
        const domain = getCustomDomainOrWebUrl(domains);
        // 创建链接时拼接url，避免第一次取不到正确的url
        if (!immediately) {
          const copyContent = `${domain}/web/invitation/${encodeURIComponent(code)}`;
          copyText2Clipboard(copyContent)
            .then(() => {
              tips.success(i18n_t('邀请链接已复制到剪切板'));
            })
            .catch(() => {
              tips.error(i18n_t('邀请链接复制失败'));
            });
        }
        reporter.report('background_user_manage', {
          add_type: 'business',
          add_result: 1,
        });
      } catch (error) {
        tips.error(i18n_t('生成邀请链接失败'));
        reporter.report('background_user_manage', {
          add_type: 'business',
          add_result: 0,
        });
      }
    },
    async checkIfHasInviteUrl(immediately?: boolean) {
      try {
        // 1. 查询邀请码
        const {
          data: { code },
        } = await smh.userAPI.getInviteCode();
        // 2. 查询邀请码是否已停止
        methods.getInviteCodeInfo(code);
      } catch (error) {
        if (immediately) {
          methods.generateInviteUrl(immediately);
        } else {
          inviteUrlInfo.codeStatus = GenerateCodeStatus.NOT_GENERATED;
        }
      }
    },
    async getInviteCodeInfo(code: string) {
      try {
        const { data: codeInfo } = await smh.userAPI.getInviteCodeInfo({
          code,
        });
        // 拿到当前企业的配置信息，这个是可加入的用户数量F
        const { userCountLimit } = store.getters.editionConfig;
        if (codeInfo.enabled && !codeInfo.expired) {
          inviteUrlInfo.codeStatus = GenerateCodeStatus.IS_START;
        } else {
          inviteUrlInfo.codeStatus = GenerateCodeStatus.IS_STOP;
        }
        inviteUrlInfo.enabled = codeInfo.enabled;
        inviteUrlInfo.expired = codeInfo.expired;
        inviteUrlInfo.code = codeInfo.code;
        inviteUrlInfo.domains = codeInfo.domains;
        inviteUrlInfo.expireTime = codeInfo.expireTime;
        inviteUrlInfo.invitedCount = codeInfo.invitedCount;
        inviteUrlInfo.userLimitReached = codeInfo.currentOrgUserCount >= userCountLimit;
        inviteUrlInfo.groupName = codeInfo.organization.name;
        // 存量数据不返回allowPersonalSpace和personalSpaceQuotaSize，取初始化设置
        inviteUrlInfo.allowPersonalSpace = codeInfo.allowPersonalSpace ?? defaultUserOptions.allowPersonalSpace;
        inviteUrlInfo.personalSpaceQuotaSize =
          codeInfo.personalSpaceQuotaSize ?? defaultUserOptions.personalSpaceQuotaSize;
        // 获取当前链接对应的空间设置
        spaceSizeInfo.allowPersonalSpace = codeInfo.allowPersonalSpace ?? defaultUserOptions.allowPersonalSpace;
        spaceSizeInfo.personalSpaceQuotaSize =
          codeInfo.personalSpaceQuotaSize ?? defaultUserOptions.personalSpaceQuotaSize;
      } catch (error) {
        inviteUrlInfo.codeStatus = GenerateCodeStatus.NOT_GENERATED;
      }
    },
    async updateInviteCodeInfo(type: 'stop' | 'recovery' | string | undefined, _params?: Object) {
      const params = {
        code: inviteUrlInfo.code,
        allowPersonalSpace: spaceSizeInfo.allowPersonalSpace,
        personalSpaceQuotaSize: spaceSizeInfo.personalSpaceQuotaSize,
      };
      if (type === 'stop') {
        Object.assign(params, {
          expireTime: dayjs(Date.now()).format('YYYY-MM-DD HH:mm'),
          enabled: false,
        });
      } else if (type === 'recovery') {
        Object.assign(params, {
          expireTime: dayjs(Date.now()).add(1, 'days').format('YYYY-MM-DD HH:mm'),
          enabled: true,
        });
      } else {
        Object.assign(params, _params);
      }
      try {
        await smh.userAPI.updateInviteCode(params);
        methods.getInviteCodeInfo(inviteUrlInfo.code);
        if (type === 'recovery') {
          reporter.report('background_user_manage', {
            add_type: 'business',
            add_result: 1,
          });
        }
        tips.success(i18n_t('设置成功'));
      } catch (error) {
        tips.error(error as MsgError);
        if (type === 'recovery') {
          reporter.report('background_user_manage', {
            add_type: 'business',
            add_result: 0,
          });
        }
      }
    },
    // 重新生成邀请码code：需先删除旧code再重新生成
    async regenerateInviteUrl() {
      // 删除原inviteUrlInfo.code
      try {
        await smh.userAPI.deleteInviteCode({
          code: inviteUrlInfo.code,
        });
        methods.generateInviteUrl();
      } catch (err: MsgError) {
        tips.error(err);
      }
    },
  };
  return {
    loadingUrlInfo,
    inviteUrlInfo,
    spaceSizeInfo,
    methods,
  };
}
